import _ from 'lodash'
import { Component, Structure } from '../types'
import type {
	LayoutRefs,
	PageJson,
	SingleLayoutData,
	TransformationsDataMap,
	TransformData,
	UnresolvedVariantRelation,
} from '@wix/thunderbolt-becky-types'

type StructureAndData = { data: PageJson['data']; structure: Structure }

const fixLayout = (
	layoutData: PageJson['data']['layout_data'],
	layoutItmToCompId: Record<string, Component>
): PageJson['data']['layout_data'] => {
	return _.mapValues(layoutData, (layout) => {
		if (layout.type === 'SingleLayoutData') {
			const layoutId = layout.id

			const comp = layoutItmToCompId[layoutId]

			if (!comp) {
				return layout
			}
			const compType = comp.componentType

			let newLayout = { ...layout }

			if (compType.includes('FooterContainer') || compType.includes('HeaderContainer')) {
				const isFixed = layout.itemLayout?.type === 'FixedPositionItemLayout'
				newLayout = ({
					...newLayout,
					itemLayout: {
						...newLayout.itemLayout,
						type: 'MasterPageItemLayout',
						isFixed: isFixed ? 'sticky' : 'relative',
					},
				} as unknown) as SingleLayoutData

				if (compType.includes('HeaderContainer')) {
					newLayout = ({
						...newLayout,
						itemLayout: {
							...newLayout.itemLayout,
							top: newLayout.itemLayout?.margins?.top,
							margins: {},
						},
					} as unknown) as SingleLayoutData
				}
			}

			return newLayout
		}

		return layout
	})
}

const getLayoutItmToCompId = ({ structure, data }: StructureAndData): Record<string, Component> => {
	const compIdToDefaultLayout = _.mapValues(structure, (comp) => {
		// SITE_PAGES is missing layoutQuery
		if (!comp.layoutQuery) {
			return { layouts: [], comp }
		}

		const layoutId = comp.layoutQuery!.replace('#', '')
		const refArray = data.layout_data[layoutId]
		const layoutItems = (refArray as LayoutRefs).values.map((v) => v.replace('#', ''))

		const mobileLayouts = []
		if (layoutItems.length === 2) {
			mobileLayouts.push((data.layout_data[layoutItems[1]] as UnresolvedVariantRelation).to.replace('#', ''))
		} else if (data.layout_data[layoutItems[0]].type === 'VariantRelation') {
			mobileLayouts.push((data.layout_data[layoutItems[0]] as UnresolvedVariantRelation).to.replace('#', ''))
		}

		return {
			layouts: [refArray.id, ...layoutItems, ...mobileLayouts],
			comp,
		}
	})
	return _.values(compIdToDefaultLayout).reduce<Record<string, Component>>((acc, { comp, layouts }) => {
		layouts.forEach((v) => {
			acc[v] = comp
		})
		return acc
	}, {})
}

const fixStructure = (flatStructure: StructureAndData['structure']): StructureAndData['structure'] => {
	return _.mapValues(flatStructure, (comp) => {
		return {
			...comp,
			layout: {},
		}
	})
}

const fixTransformationsData = (
	layoutData: PageJson['data']['layout_data'],
	layoutItemToComp: Record<string, Component>,
	flatStructure: Structure
) => {
	const fixedStructure: Structure = { ...flatStructure }
	const transformationsData: TransformationsDataMap = {}

	_.forEach(layoutData, (layout, layoutId) => {
		const rotate = layoutItemToComp[layoutId]?.layout?.rotationInDegrees
		if (layout.type === 'SingleLayoutData' && rotate) {
			const transformationId = `transformation${layoutId.replace('layout', '')}`
			const compTransformationQuery = fixedStructure[layoutItemToComp[layoutId].id].transformationQuery

			if (!compTransformationQuery || !transformationsData[compTransformationQuery]) {
				transformationsData[`${transformationId}-ref-array`] = {
					id: `${transformationId}-ref-array`,
					type: 'RefArray',
					// @ts-ignore
					values: [`#${transformationId}`],
				}

				fixedStructure[layoutItemToComp[layoutId].id].transformationQuery = `${transformationId}-ref-array`
			} else {
				// @ts-ignore
				transformationsData[`${transformationId}-variant-relation`] = {
					id: `${transformationId}-variant-relation`,
					type: 'VariantRelation',
					from: `#${layoutItemToComp[layoutId].id}`,
					to: `#${transformationId}`,
					variants: ['#MOBILE-VARIANT'],
				} as UnresolvedVariantRelation

				const transformationRefArray = transformationsData[compTransformationQuery]
				// @ts-ignore
				transformationRefArray.values.push(`${transformationId}-variant-relation`)
			}

			transformationsData[transformationId] = {
				id: transformationId,
				type: 'TransformData',
				rotate,
			} as TransformData
		}
	})

	return { transformationsData, fixedStructure }
}

const oneDocFixerForPage = (structureAndData: StructureAndData): StructureAndData => {
	const layoutItmToCompId = getLayoutItmToCompId(structureAndData)
	const { data, structure } = structureAndData

	const fixedLayoutData = fixLayout(data.layout_data, layoutItmToCompId)
	const { transformationsData, fixedStructure } = fixTransformationsData(
		fixedLayoutData,
		layoutItmToCompId,
		structure
	)
	const fixedFlatStructure = fixStructure(fixedStructure)

	return {
		structure: fixedFlatStructure,
		data: {
			...data,
			layout_data: fixedLayoutData,
			transformations_data: transformationsData,
		},
	}
}

export default oneDocFixerForPage
